import React, { useContext, useState } from 'react';
import ConsentListContext from '../../Context/ConsentContext';
import { useTheme } from '@emotion/react';
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Divider,
	Paper,
	Stack,
	Typography,
	useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import sizeFontCustom from '../../Discovery/fontSize.styles';
import Field from './Field';
import {
	capitalize,
	dateFormat,
	getConsentModeText,
	getFrequencyUnit,
} from '../../../hepler/functions';
import ChipSet from './ChipSet';
import CustomPopover from '../../Popover/CustomPopover';
import { keyboardFocusVisibleSx } from '../../../utils/a11y.utils';
import AccountInfoContent from '../../Popover/AccountInfoContent';
import ConsentRequestedExpires from './ConsentRequestedExpires';
import ConsentCardLSPHeading from '../ConsentCardLSPHeading';
import PurposeContent from '../../Popover/PurposeContent';
import AuthApis from '../../../hepler/authApis.service';
import { useTranslation } from '../../../hooks/useTranslation';
import { compareConsentOptions } from '../../../utils/helper.utils';
import DialogBox from '../../Dialog Box/Dialog Box';
import Drawer from '../../Drawer/Drawer';
import Footer from '../../Sections/Footer';
import GrievanceDetails from '../GrievanceDetails';
import { getEntityThemedCtaInlineStyle } from '../../../utils/ctaContrast.utils';

const ConsentCardFull = (props) => {
	//Props
	const { t } = useTranslation();
	const {
		consentDetails,
		customDetails,
		id,
		selectedConsentsState,
		handleIsActive,
		isMultiConsent,
		children,
		isLast,
		onInfoClick,
		isSequentialFlow,
	} = props;

	//Context
	const consentContext = useContext(ConsentListContext);
	const consentHandle = consentDetails?.ConsentHandle;
	const authApis = new AuthApis();
	//Priority Consents
	const priorityCodes = global?.priorityConsents?.split(',') || [];
	const isPriority = (consentDetails) => {
		return !authApis.isCheckboxRequired(consentDetails);
	};

	const isActiveFlag = isPriority(consentDetails)
		? true
		: selectedConsentsState?.includes(consentHandle) &&
		  !isPriority(consentDetails)
		? true
		: false;
	const consentList = consentContext.consentList;
	const numberOfConsents = consentList.length;
	const [showMoreInfo, setShowMoreInfo] = useState(false);
	const [openGrievance, setOpenGrievance] = useState(false);
	//Themeing
	const theme = useTheme();
	const mobileScreen = useMediaQuery(theme.breakpoints.down('md'));
	
	// Check for dark theme - using your established pattern
	const isDark = customDetails?.darkTheme === true || theme.palette.mode === 'dark';

	const isLsp = global.institutionType === 'LSP';

	const consentToggleLabel =
		numberOfConsents > 1
			? t('common.labels.selectConsent', {
					current: id,
					total: numberOfConsents,
				})
			: t('paymentConsent.titles.consentPurpose');

	
	return (
		<Box height={'none'} sx={{ mx: 1, mb: 1 }}>
			<Paper
				elevation={0}
				sx={{
					m: mobileScreen ? '0' : '0.25rem 0rem 0.25rem 0',
					flex: 1,
					height: '100%',
					width: mobileScreen ? '100%' : 'auto',
					backgroundColor: isActiveFlag
						? isSequentialFlow
							? isDark
								? '#161616'
								: '#FFFFFF'
							: theme.palette.primary.light
						: isDark
						? '#161616'
						: '#FFFFFF',
					// Add border for dark theme visibility
					border: isDark ? `1px solid ${isDark ? '#7A8185' : '#D8E1EE'}` : 'none',
				}}
			>
				<Box
					sx={{
						boxShadow: numberOfConsents === 1 || isActiveFlag ? 5 : 1,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'flex-start',
						padding: mobileScreen
							? `0.5rem 0.5rem ${isLast ? '5rem' : '1rem'} 0.5rem`
							: '1.25rem 2.75rem 1rem 1.5rem',

						borderTop: isActiveFlag
							? `5px solid ${theme.palette.primary.main}`
							: '',

						gap: mobileScreen ? '0.5rem' : '0.5rem',
						width: '100%',
						borderBottomRightRadius: mobileScreen ? 0 : 'inherit',
						borderBottomLeftRadius: mobileScreen ? 0 : 'inherit',
					}}
				>
					<Box
						width={'100%'}
						onClick={
							isMultiConsent && !isPriority(consentDetails)
								? () => handleIsActive(consentDetails.ConsentHandle)
								: null
						}
					>
						{isLsp && (
							<>
								<ConsentCardLSPHeading
									isActiveFlag={isActiveFlag}
									consentDetails={consentDetails}
								/>
								<Divider variant="light" flexItem sx={{ mb: 1 }} />
							</>
						)}

						<Stack
							justifyContent={'space-between'}
							gap={mobileScreen ? '1rem' : '1.625rem'}
							width={'100%'}
						>
							<Box
								display={'flex'}
								justifyContent={'space-between'}
								alignItems={'center'}
							>
								{/* Number */}
								<Box
									display={'flex'}
									flexDirection={'row'}
									width="100%"
									height={isMultiConsent && mobileScreen ? '100%' : 'auto'}
									alignItems={'center'}
								>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											pl: mobileScreen
												? '0.25rem'
												: isLsp
												? '0.25rem'
												: '0.5rem',
											flexGrow: 1,
											maxHeight: '100%',
										}}
									>
										<div>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
												}}
											>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 4,
													}}
												>
													{numberOfConsents > 1 && isMultiConsent && !isLsp ? (
														<Chip
															variant="outlined"
															size="small"
															label={`${id} of ${numberOfConsents} `}
															sx={{
																border: `1px solid ${isDark ? '#757575' : theme.palette.black60}`,
																fontWeight: 700,
																color: isDark ? '#9AA3A8' : theme.palette.black60,
																'& .MuiChip-label': {
																	fontSize: '0.675rem',
																},
															}}
														/>
													) : null}
													<Typography
														sx={{
															...sizeFontCustom.subHeading,
															fontFamily: customDetails?.fontFamily,
															textAlign: 'left',
															color: customDetails?.primaryTextColor ?? (isDark ? '#FFFFFF' : '#000000')
														}}
													>
														{t('paymentConsent.titles.consentPurpose')}
													</Typography>
													<CustomPopover
														fieldName="purpose"
														isActiveFlag={isActiveFlag}
														onInfoClick={onInfoClick}
														content={
															<PurposeContent
																code={consentDetails?.Purpose?.code}
																category={
																	consentDetails?.Purpose?.Category?.type
																}
															/>
														}
													/>
												</div>
												{numberOfConsents > 1 &&
												isMultiConsent &&
												!isLsp &&
												!isPriority(consentDetails) ? (
													<Checkbox
														color="primary"
														checked={isActiveFlag}
														name={consentDetails?.ConsentHandle}
														onChange={() =>
															handleIsActive(consentDetails.ConsentHandle)
														}
														onClick={(event) => event.stopPropagation()}
														inputProps={{ 'aria-label': consentToggleLabel }}
													/>
												) : null}
											</div>
										</div>
										<Typography
											sx={{
												...sizeFontCustom.field,
												// maxWidth: mobileScreen ? '60vw' : '19vw',
												fontFamily: customDetails?.fontFamily,
												textAlign: 'left',
												fontWeight: 700,
												color: customDetails?.primaryTextColor ?? (isDark ? '#FFFFFF' : '#000000')
											}}
										>
											{compareConsentOptions(consentDetails, global.consentOptions, t)}
										</Typography>
									</Box>
								</Box>
							</Box>
						</Stack>
					</Box>
					<Box sx={{ width: '100%' }}>
						{!isLsp && <Divider variant="light" flexItem sx={{ mb: 1 }} />}
						<Stack
							sx={{
								px: '0.25rem',
								mx: '0rem',
								mt: '0rem',
								width: '100%',
								gap: mobileScreen ? '0rem' : '0.2rem',
							}}
						>
							<div
								style={{
									width: '100%',
									display: 'grid',
									gridTemplateColumns: `${
										mobileScreen ? 'repeat(2, 1fr)' : 'repeat(6, 1fr)'
									}`,
									columnGap: '2rem',
									margin: '0 0 0.4rem 0',
								}}
							>
								<Field
									property={t('paymentConsent.purposeTexts.l1Parameters.dataFetchFrequency')}
									value={
										consentDetails?.fetchType === 'ONETIME'
											? 'ONE TIME'
											: `${
													consentDetails?.Frequency?.value
											  } times ${getFrequencyUnit(
													consentDetails?.Frequency?.unit
											  )}`
									}
									customDetails={customDetails}
								/>
								<Field
									property={t('paymentConsent.purposeTexts.l1Parameters.dataUse')}
									value={getConsentModeText(
										consentDetails?.mode,
										consentDetails?.DataLife?.value,
										consentDetails?.DataLife?.unit
									)}
									customDetails={customDetails}
								/>
								<Field
									property={t('paymentConsent.purposeTexts.l1Parameters.dataFetchFrom')}
									value={dateFormat(consentDetails?.DataDateTimeRange.from)}
									customDetails={customDetails}
								/>
								<Field
									property={t('paymentConsent.purposeTexts.l1Parameters.dataFetchUntil')}
									value={dateFormat(consentDetails?.DataDateTimeRange?.to)}
									customDetails={customDetails}
								/>
								{!mobileScreen && <ConsentRequestedExpires {...props} />}
							</div>
							{mobileScreen && (
								<div
									style={{
										display: 'grid',
										gridTemplateColumns: '1fr 1fr',
										columnGap: '2rem',
										margin: '0 0 0.4rem 0',
									}}
								>
									{mobileScreen && <ConsentRequestedExpires {...props} />}
								</div>
							)}
						</Stack>
					</Box>
					<Divider variant="light" flexItem sx={{ mt: '-0.75rem' }} />
					<Button
						type="button"
						onClick={() => setShowMoreInfo(!showMoreInfo)}
						disableRipple
						aria-expanded={showMoreInfo}
						sx={{
							fontFamily: customDetails?.fontFamily,
							color: isDark ? '#FFFFFF' : '#000000',
							'& .MuiButton-endIcon': {
								marginLeft: '0 !important',
							},
							'&:hover': {
								backgroundColor: 'transparent',
							},
							p: '0.25rem',
							backgroundColor: 'transparent',
							fontSize: '12px',
							textTransform: 'none',
							borderRadius: '4px',
							...keyboardFocusVisibleSx,
						}}
						endIcon={<ExpandMoreIcon sx={{ 
							transform: showMoreInfo ? 'rotate(180deg)' : 'rotate(0deg)',
							transition: 'transform 0.5s ease',
							color: isDark ? '#FFFFFF' : '#000000'
						}} />}
					>
						More Info
					</Button>
					{showMoreInfo && <div
						style={{
							display: 'grid',
							gridTemplateColumns: mobileScreen ? '1fr' : '1fr 1fr 1fr',
							gap: mobileScreen ? '0.5rem' : '1rem',
						}}
					>
						<Field
							property={t('paymentConsent.purposeTexts.l1Parameters.accountInformation')}
							customDetails={customDetails}
							popoverComponent={
								<CustomPopover
									fieldName="accountInfo"
									onInfoClick={onInfoClick}
									isActiveFlag={isActiveFlag}
									content={<AccountInfoContent />}
								/>
							}
							tooltipText={true}
						>
							<div
								style={{
									display: 'flex',
									flexWrap: 'wrap',
									gap: 1,
									margin: '0 0 0.4rem 0',
								}}
							>
								{consentDetails?.consentTypes.map((type) => (
									<Chip
										key={type}
										variant="outlined"
										size="small"
										label={capitalize(type)}
										sx={{
											mr: 0.5,
											mt: 0.5,
											color: isDark ? '#9AA3A8' : theme.palette.black60,
											borderColor: isDark ? '#757575' : theme.palette.black60,
											fontSize: '0.675rem',
										}}
									/>
								))}
							</div>
						</Field>
						<Field
							property={t('paymentConsent.purposeTexts.l1Parameters.accountTypeRequested')}
							customDetails={customDetails}
							styles={{ width: '100%' }}
						>
							<ChipSet customDetails={customDetails} fiTypes={consentDetails?.fiTypes} />
						</Field>
						<Field
							property="Grievance Details"
							customDetails={customDetails}
							styles={{ width: '100%' }}
						>
							<Typography
								component="button"
								type="button"
								onClick={() => setOpenGrievance(true)}
								sx={{
									...sizeFontCustom.cardText,
									fontFamily: customDetails?.fontFamily,
									textAlign: 'left',
									color: theme.palette.primary.main,
									cursor: 'pointer',
									textDecoration: 'underline',
									background: 'none',
									border: 'none',
									padding: '0.25rem',
									borderRadius: '4px',
									'&:hover': {
										color: theme.palette.primary.dark,
									},
									...keyboardFocusVisibleSx,
								}}
							>
								{t('common.labels.viewGrievanceContact')}
							</Typography>
						</Field>
					</div>
}
					<div className="lastChildCard" />
					<div style={{ width: '100%' }}>{children}</div>
				</Box>
			</Paper>
			
			{/* Grievance Details Modal/Drawer */}
			{!mobileScreen ? (
				<DialogBox
					open={openGrievance}
					handleClose={() => setOpenGrievance(false)}
					title="Grievance Redressal"
					boxPadding="1.5rem"
					customDetails={customDetails}
					isGrievance={true}
				>
					<GrievanceDetails
						handleClose={() => setOpenGrievance(false)}
						customDetails={customDetails}
						fiuInfo={consentDetails?.FIU}
					/>
				</DialogBox>
			) : (
				<Drawer
					open={openGrievance}
					setOpen={setOpenGrievance}
					anchor="bottom"
					height="auto"
					customDetails={customDetails}
					showCloseBtn={false}
					ariaLabelledBy="grievance-drawer-title"
					ariaDescribedBy="grievance-drawer-content"
				>
					<Box
						sx={{
							padding: '1.5rem',
							fontFamily: customDetails?.fontFamily,
							backgroundColor: isDark
								? '#161616'
								: customDetails?.secondaryColor || '#fff',
						}}
					>
						<Typography
							component="h2"
							id="grievance-drawer-title"
							sx={{
								fontSize: '1rem',
								fontWeight: 600,
								mb: '1rem',
								fontFamily: customDetails?.fontFamily,
								color: isDark ? '#FFFFFF' : '#000000',
							}}
						>
							Grievance Redressal
						</Typography>
						<Box id="grievance-drawer-content">
							<GrievanceDetails
								customDetails={customDetails}
								fiuInfo={consentDetails?.FIU}
							/>
						</Box>
						<Divider
							sx={{
								mt: '1.5rem',
								mb: '1.5rem',
								marginLeft: '-1.5rem',
								marginRight: '-1.5rem',
								width: 'calc(100% + 3rem)',
							}}
						/>
						<Box
							component="button"
							type="button"
							onClick={() => setOpenGrievance(false)}
							style={getEntityThemedCtaInlineStyle(customDetails, isDark, {
								width: '100%',
								padding: '10px 0',
								borderRadius: customDetails?.borderRadius || '6px',
								border: 'none',
								cursor: 'pointer',
								fontFamily: customDetails?.fontFamily,
								fontSize: '0.9375rem',
								fontWeight: 500,
								textTransform: 'capitalize',
								boxShadow: 'none',
							})}
						>
							{t('consentRenewal.buttons.understood')}
						</Box>
					</Box>
					<Footer customDetails={customDetails} />
				</Drawer>
			)}
		</Box>
	);
};

export default ConsentCardFull;
